import React, { useState, useEffect } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { Text } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useForm } from 'react-hook-form';
import { usePatientForm } from '@/contexts/PatientFormContext';
import { useGetPatientFormDataQuery } from '@/data/data';
import PatientAuthService from '@/services/PatientAuthService';
import BaseContainer from '@/components/BaseContainer';
import ScreenMargin from '@/components/ScreenMargin';
import Form from '@/components/Form/Form';
import FTextInput from '@/components/Form/TextInput';
import DatePickerInput from '@/components/Form/DatePickerInput';
import IconListInput from '@/components/Form/IconListInput';
import TimePickerInput from '@/components/Form/TimePickerInput';
import DurationSliderInput from '@/components/Form/DurationSliderInput';
import { FormSection } from '@/components/Form/FormSectionContext';
import FormSectionFooter from '@/components/Form/FormSectionFooter';
import { Color } from '@/GlobalStyles';
import useFormStyle from '@/components/Styles/useFormStyle';
import { useToast } from 'react-native-toast-notifications';
import { AvailableValidationRules } from '@/components/Form/validation';
import { useTranslation } from 'react-i18next';
import { FormData } from '@/config/patient-auth';
import ObjectHelper from '@/utils/ObjectHelper';
import Button from '@/components/Button';
import { IconButton } from 'react-native-paper';

interface LocalFormData {
  // Biométrie
  nom?: string;
  prenom?: string;
  sexe?: number;
  birthday?: Date;
  taille?: number;
  photicEffect?: number;
  glasses?: number;

  // Habitudes - Horaires
  heureReveil?: Date;
  heureCoucher?: Date;
  tempsSieste?: Date;

  // Habitudes - Vision de près
  tempsLecture?: Date;
  tempsTablette?: Date;
  tempsSmartphone?: Date;
  tempsAutreVisionPres?: Date;

  // Habitudes - Vision intermédiaire
  tempsDiscussion?: Date;
  tempsJeuxSociete?: Date;
  tempsOrdinateur?: Date;
  tempsCuisine?: Date;

  // Habitudes - Vision de loin
  tempsConduite?: Date;
  tempsTelevision?: Date;

  // Activités préférentielles
  activitesPreferentielles?: Array<{
    nom?: string;
    distanceMin?: string;
    distanceMax?: string;
  }>;
}

export default function FormScreen() {
  const router = useRouter();
  const toast = useToast();
  const { t } = useTranslation();
  const { updateForm, saveProgress, setCurrentStep } = usePatientForm();

  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [nbActivitesPref, setNbActivitesPref] = useState(1);
  const styles = useFormStyle();

  // Récupérer le token depuis AsyncStorage et vérifier l'accès
  useEffect(() => {
    const loadToken = async () => {
      const tokenData = await PatientAuthService.getTokenRaw();
      if (tokenData?.token) {
        setToken(tokenData.token);
      } else {
        console.warn('[Form] Accès refusé: aucun token valide');
        router.replace('/patient/form/error-access');
      }
    };
    loadToken();
  }, [router]);

  // Charger les données patient depuis GraphQL
  const { data: patientData, loading: dataLoading } = useGetPatientFormDataQuery({
    variables: { token: token || '' },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const patient = patientData?.patientTempTokens?.[0]?.patient_tokens;
  const formDataFromGraphQL = patientData?.patientTempTokens?.[0]?.formData;
  const habitudesFromDB = patientData?.patientTempTokens?.[0]?.patient_tokens?.habitudes?.[0];
  const activitesFromDB =
    patientData?.patientTempTokens?.[0]?.patient_tokens?.activitePreferentielles;

  const formMethods = useForm<LocalFormData>();
  const hasInitializedRef = React.useRef(false);

  // Charger les données depuis GraphQL
  useEffect(() => {
    // Ne s'exécuter qu'une seule fois quand les données sont chargées
    if (dataLoading || hasInitializedRef.current) return;
    if (!patient) return; // Attendre que les données patient soient disponibles

    hasInitializedRef.current = true;

    console.log('[Form] FormDataFromGraphQL:', formDataFromGraphQL);
    console.log('[Form] Patient:', patient);
    console.log('[Form] HabitudesFromDB:', habitudesFromDB);
    console.log('[Form] ActivitesFromDB:', activitesFromDB);

    // Logique de priorité simplifiée comme dans les anciens fichiers :
    // 1. formDataFromGraphQL.form (données saisies précédemment) - si non vide
    // 2. habitudesFromDB + patient (données de la DB)

    // Vérifier si formDataFromGraphQL.form a des données significatives
    const hasFormDataFromGraphQL =
      formDataFromGraphQL?.form && Object.keys(formDataFromGraphQL.form).length > 0;

    // Créer les valeurs par défaut de base avec ObjectHelper.createTimeValues
    const baseTimeValues = ObjectHelper.createTimeValues({
      heureReveil: '00:00',
      heureCoucher: '00:00',
      tempsSieste: '00:00',
      tempsLecture: '00:00',
      tempsTablette: '00:00',
      tempsSmartphone: '00:00',
      tempsAutreVisionPres: '00:00',
      tempsDiscussion: '00:00',
      tempsJeuxSociete: '00:00',
      tempsOrdinateur: '00:00',
      tempsCuisine: '00:00',
      tempsConduite: '00:00',
      tempsTelevision: '00:00',
    });

    // Construire les valeurs par défaut selon la priorité
    let newDefaultValues: LocalFormData;

    if (hasFormDataFromGraphQL) {
      // Cas 1: On a des données de session précédente
      console.log('[Form] Chargement depuis formDataFromGraphQL');
      newDefaultValues = {
        nom: formDataFromGraphQL.form.nom || '',
        prenom: formDataFromGraphQL.form.prenom || '',
        sexe:
          formDataFromGraphQL.form.sexe === 'M'
            ? 1
            : formDataFromGraphQL.form.sexe === 'F'
              ? 2
              : undefined,
        birthday: formDataFromGraphQL.form.birthday
          ? new Date(formDataFromGraphQL.form.birthday)
          : undefined,
        taille: formDataFromGraphQL.form.taille || undefined,
        photicEffect:
          formDataFromGraphQL.form.photicEffect !== undefined
            ? formDataFromGraphQL.form.photicEffect
              ? 1
              : 0
            : 0,
        glasses:
          formDataFromGraphQL.form.glasses !== undefined
            ? formDataFromGraphQL.form.glasses
              ? 1
              : 0
            : 0,
        ...baseTimeValues,
        ...ObjectHelper.createTimeValues(formDataFromGraphQL.form),
        activitesPreferentielles: formDataFromGraphQL.form.activitesPreferentielles || [],
      };
    } else {
      // Cas 2: Première visite, charger depuis patient + habitudes
      console.log('[Form] Chargement depuis patient + habitudesFromDB');
      newDefaultValues = {
        nom: patient?.nom || '',
        prenom: patient?.prenom || '',
        sexe: patient?.sexe === 1 ? 1 : patient?.sexe === 2 ? 2 : undefined,
        birthday: patient?.birthday ? new Date(patient.birthday) : undefined,
        taille: patient?.size || undefined,
        photicEffect: patient?.photicEffect ? 1 : 0,
        glasses: patient?.glasses ? 1 : 0,
        ...baseTimeValues,
        ...ObjectHelper.createTimeValues(habitudesFromDB),
        activitesPreferentielles: activitesFromDB || [],
      };
    }

    console.log('[Form] DefaultValues créées:', newDefaultValues);

    // IMPORTANT: Appliquer les valeurs au formulaire avec reset()
    formMethods.reset(newDefaultValues);

    // Mettre à jour le contexte avec les données initiales
    const contextData: Partial<FormData> = {
      nom: newDefaultValues.nom,
      prenom: newDefaultValues.prenom,
      sexe: newDefaultValues.sexe === 1 ? 'M' : newDefaultValues.sexe === 2 ? 'F' : undefined,
      birthday: newDefaultValues.birthday,
      taille: newDefaultValues.taille,
      photicEffect: newDefaultValues.photicEffect === 1,
      glasses: newDefaultValues.glasses === 1,
      heureReveil: newDefaultValues.heureReveil,
      heureCoucher: newDefaultValues.heureCoucher,
      tempsSieste: newDefaultValues.tempsSieste,
      tempsLecture: newDefaultValues.tempsLecture,
      tempsTablette: newDefaultValues.tempsTablette,
      tempsSmartphone: newDefaultValues.tempsSmartphone,
      tempsAutreVisionPres: newDefaultValues.tempsAutreVisionPres,
      tempsDiscussion: newDefaultValues.tempsDiscussion,
      tempsJeuxSociete: newDefaultValues.tempsJeuxSociete,
      tempsOrdinateur: newDefaultValues.tempsOrdinateur,
      tempsCuisine: newDefaultValues.tempsCuisine,
      tempsConduite: newDefaultValues.tempsConduite,
      tempsTelevision: newDefaultValues.tempsTelevision,
      activitesPreferentielles: newDefaultValues.activitesPreferentielles,
    };
    updateForm(contextData);

    // Ajuster le nombre d'activités
    const activites = newDefaultValues.activitesPreferentielles || [];
    if (activites.length > 0) {
      setNbActivitesPref(activites.length);
    }
  }, [dataLoading, patient]); // Se déclenche quand dataLoading change ou quand patient arrive

  useEffect(() => {
    setCurrentStep('form');
  }, []);

  const handleNext = async () => {
    const formValues = formMethods.getValues();

    // Validation biométrie
    if (!formValues.sexe) {
      toast.show(t('patient.selectGenderError'), { type: 'warning', placement: 'bottom' });
      return;
    }

    if (!formValues.birthday) {
      toast.show(t('patient.enterBirthdateError'), { type: 'warning', placement: 'bottom' });
      return;
    }

    if (!formValues.taille || formValues.taille < 100 || formValues.taille > 250) {
      toast.show(t('patient.enterHeightError'), { type: 'warning', placement: 'bottom' });
      return;
    }

    setLoading(true);
    try {
      // Convertir les données locales en FormData pour le contexte
      const updatedFormData: Partial<FormData> = {
        // Biométrie
        nom: formValues.nom,
        prenom: formValues.prenom,
        sexe: formValues.sexe === 1 ? 'M' : 'F',
        birthday: formValues.birthday,
        taille: formValues.taille,
        photicEffect: formValues.photicEffect === 1,
        glasses: formValues.glasses === 1,

        // Habitudes
        heureReveil: formValues.heureReveil,
        heureCoucher: formValues.heureCoucher,
        tempsSieste: formValues.tempsSieste,
        tempsLecture: formValues.tempsLecture,
        tempsTablette: formValues.tempsTablette,
        tempsSmartphone: formValues.tempsSmartphone,
        tempsAutreVisionPres: formValues.tempsAutreVisionPres,
        tempsDiscussion: formValues.tempsDiscussion,
        tempsJeuxSociete: formValues.tempsJeuxSociete,
        tempsOrdinateur: formValues.tempsOrdinateur,
        tempsCuisine: formValues.tempsCuisine,
        tempsConduite: formValues.tempsConduite,
        tempsTelevision: formValues.tempsTelevision,
        activitesPreferentielles: formValues.activitesPreferentielles,
      };

      updateForm(updatedFormData);
      await saveProgress();
      router.push('/patient/form/confirmation');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.show(t('patient.saveError'), { type: 'danger', placement: 'bottom' });
    } finally {
      setLoading(false);
    }
  };

  const addActivite = () => {
    setNbActivitesPref(nbActivitesPref + 1);
  };

  const removeActivite = (index: number) => {
    const currentActivites = formMethods.getValues('activitesPreferentielles') || [];
    const newActivites = currentActivites.filter((_, i) => i !== index);
    formMethods.setValue('activitesPreferentielles', newActivites);
    setNbActivitesPref(nbActivitesPref - 1);
  };

  if (dataLoading) {
    return (
      <BaseContainer>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="#678aae" />
          <Text style={{ marginTop: 16 }}>{t('patient.loading')}</Text>
        </View>
      </BaseContainer>
    );
  }

  return (
    <BaseContainer
      withScrollView={'keyboardAware'}
      outerViewProps={{ style: { backgroundColor: '#fff' } }}
    >
      <ScreenMargin>
        <Form<LocalFormData> {...formMethods}>
          <View style={styles?.bodyForm}>
            {/* Section 1: Biométrie */}
            <FormSection sectionOrder={1}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.biometricData')}</Text>

                <FTextInput
                  name="nom"
                  label={t('patient.lastName')}
                  withMargin={false}
                  containerStyle={styles.input2Col}
                />

                <FTextInput
                  name="prenom"
                  label={t('patient.firstName')}
                  withMargin={false}
                  containerStyle={styles.input2Col}
                />

                <IconListInput
                  name="sexe"
                  label={t('patient.gender')}
                  withHelpButton={true}
                  helpFieldName="sexePatient"
                  data={[
                    { value: 1, label: t('patient.male'), icon: 'gender-male' },
                    { value: 2, label: t('patient.female'), icon: 'gender-female' },
                  ]}
                  containerStyle={styles.input2Col}
                  onChangeValue={(value) => updateForm({ sexe: value === 1 ? 'M' : 'F' })}
                />

                <View style={styles.input2Col}></View>

                <FTextInput
                  name="taille"
                  label={t('patient.height')}
                  withHelpButton={true}
                  helpFieldName="taillePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  keyboardType="number-pad"
                  onChangeValue={(value) =>
                    updateForm({
                      taille:
                        (typeof value === 'string' ? parseInt(value) : (value as number)) ||
                        undefined,
                    })
                  }
                />

                <DatePickerInput
                  name="birthday"
                  label={t('patient.birthdate')}
                  helpFieldName="dateNaissancePatient"
                  withHelpButton={true}
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  validators={[AvailableValidationRules.required]}
                  onChangeValue={(value) => updateForm({ birthday: (value as Date) || undefined })}
                />

                <IconListInput
                  name="photicEffect"
                  label={t('patient.acceptPhoticEffects')}
                  withHelpButton={true}
                  helpFieldName="photicEffectPatient"
                  data={[
                    {
                      value: 1,
                      label: t('patient.yes'),
                      icon: 'check-circle',
                      inactiveIcon: 'checkbox-blank-circle-outline',
                    },
                    {
                      value: 0,
                      label: t('patient.no'),
                      icon: 'close-circle',
                      inactiveIcon: 'checkbox-blank-circle-outline',
                    },
                  ]}
                  containerStyle={styles.input2Col}
                  onChangeValue={(value) => updateForm({ photicEffect: value === 1 })}
                />

                <IconListInput
                  name="glasses"
                  label={t('patient.wantGlassesIndependence')}
                  withHelpButton={true}
                  helpFieldName="glassesPatient"
                  data={[
                    {
                      value: 1,
                      label: t('patient.yes'),
                      icon: 'check-circle',
                      inactiveIcon: 'checkbox-blank-circle-outline',
                    },
                    {
                      value: 0,
                      label: t('patient.no'),
                      icon: 'close-circle',
                      inactiveIcon: 'checkbox-blank-circle-outline',
                    },
                  ]}
                  containerStyle={styles.input2Col}
                  onChangeValue={(value) => updateForm({ glasses: value === 1 })}
                />
              </View>
            </FormSection>

            {/* Section 2: Habitudes - Horaires */}
            <FormSection sectionOrder={2}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.schedule')}</Text>

                <TimePickerInput
                  name="heureReveil"
                  label={t('patient.wakeupHour')}
                  withHelpButton={true}
                  helpFieldName="heureReveilPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  validators={[AvailableValidationRules.required]}
                />

                <TimePickerInput
                  name="heureCoucher"
                  label={t('patient.bedtimeHour')}
                  withHelpButton={true}
                  helpFieldName="heureCoucherPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  validators={[AvailableValidationRules.required]}
                />

                <DurationSliderInput
                  name="tempsSieste"
                  label={t('patient.napTime')}
                  withHelpButton={true}
                  helpFieldName="tempsSiestePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  minimumMinutes={0}
                  maximumMinutes={240}
                  stepMinutes={15}
                />

                <View style={styles.input2Col}></View>
              </View>
            </FormSection>

            {/* Section 3: Vision de près */}
            <FormSection sectionOrder={3}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.nearVision')}</Text>

                <DurationSliderInput
                  name="tempsLecture"
                  label={t('patient.readingTime')}
                  withHelpButton={true}
                  helpFieldName="tempsLecturePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsTablette"
                  label={t('patient.tabletTime')}
                  withHelpButton={true}
                  helpFieldName="tempsTablettePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsSmartphone"
                  label={t('patient.smartphoneTime')}
                  withHelpButton={true}
                  helpFieldName="tempsSmartphonePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsAutreVisionPres"
                  label={t('patient.otherNearVisionTime')}
                  withHelpButton={true}
                  helpFieldName="tempsAutreVisionPresPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>

            {/* Section 4: Vision intermédiaire */}
            <FormSection sectionOrder={4}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.intermediateVision')}</Text>

                <DurationSliderInput
                  name="tempsDiscussion"
                  label={t('patient.discussionTime')}
                  withHelpButton={true}
                  helpFieldName="tempsDiscussionPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsJeuxSociete"
                  label={t('patient.boardGamesTime')}
                  withHelpButton={true}
                  helpFieldName="tempsJeuxSocietePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsOrdinateur"
                  label={t('patient.computerTime')}
                  withHelpButton={true}
                  helpFieldName="tempsOrdinateurPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsCuisine"
                  label={t('patient.cookingTime')}
                  withHelpButton={true}
                  helpFieldName="tempsCuisinePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>

            {/* Section 5: Vision de loin */}
            <FormSection sectionOrder={5}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.distanceVision')}</Text>

                <DurationSliderInput
                  name="tempsConduite"
                  label={t('patient.drivingTime')}
                  withHelpButton={true}
                  helpFieldName="tempsConduitePatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />

                <DurationSliderInput
                  name="tempsTelevision"
                  label={t('patient.tvTime')}
                  withHelpButton={true}
                  helpFieldName="tempsTelevisionPatient"
                  withMargin={false}
                  containerStyle={styles.input2Col}
                  maximumMinutes={600}
                  stepMinutes={15}
                />
              </View>
            </FormSection>

            {/* Section 6: Activités préférentielles */}
            <FormSection sectionOrder={6}>
              <View style={styles?.groupForm}>
                <Text style={styles?.titleGroupForm}>{t('patient.preferredActivities')}</Text>

                {Array.from({ length: nbActivitesPref }).map((_, index) => (
                  <>
                    <View
                      key={index}
                      style={{ width: '90%', flexDirection: 'row', flexWrap: 'wrap' }}
                    >
                      <FTextInput
                        name={`activitesPreferentielles.${index}.nom`}
                        label={`${t('patient.activity')} ${index + 1}`}
                        withHelpButton={true}
                        helpFieldName="activitePreferentiellePatient"
                        withMargin={false}
                        topLabel={true}
                        multiline={true}
                        numberOfLines={2}
                        style={{ minHeight: 60, height: undefined }}
                        contentStyle={{ minHeight: 60, paddingTop: 8, paddingBottom: 8 }}
                        containerStyle={styles.input1Col}
                      />

                      <FTextInput
                        name={`activitesPreferentielles.${index}.distanceMin`}
                        label={t('patient.minDistance')}
                        withHelpButton={true}
                        helpFieldName="activitePreferentielleDistanceMinPatient"
                        withMargin={false}
                        topLabel={true}
                        keyboardType="number-pad"
                        containerStyle={styles.input2Col}
                      />

                      <FTextInput
                        name={`activitesPreferentielles.${index}.distanceMax`}
                        label={t('patient.maxDistance')}
                        withHelpButton={true}
                        helpFieldName="activitePreferentielleDistanceMaxPatient"
                        withMargin={false}
                        topLabel={true}
                        keyboardType="number-pad"
                        containerStyle={styles.input2Col}
                      />
                    </View>

                    <IconButton
                      icon="delete"
                      iconColor={Color.error}
                      size={20}
                      onPress={() => removeActivite(index)}
                      style={{ margin: 20, marginTop: 20 }}
                    />
                  </>
                ))}

                <Button
                  mode="outlined"
                  onPress={addActivite}
                  outerStyle={{ marginTop: 16, width: 'auto' }}
                  icon="plus"
                >
                  {t('patient.addActivity')}
                </Button>
              </View>
            </FormSection>
          </View>

          <View style={styles?.footerForm}>
            <FormSectionFooter
              onPress={handleNext}
              saveLoading={loading}
              saveDisabled={loading}
              nextLabel={t('patient.next')}
            />
          </View>
        </Form>
      </ScreenMargin>
    </BaseContainer>
  );
}
